import React from 'react'
import {
  Box,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material'

function OverviewTab({ intervention, onChange, canEdit, isNew }) {
  const handleChange = (field) => (event) => {
    onChange(field, event.target.value)
  }

  return (
    <Box maxWidth="800px">
      {/* Label-value grid for single-line fields */}
      <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center">
        {/* Code */}
        <Typography variant="body2" color="text.secondary">Code</Typography>
        {isNew ? (
          <Box display="flex" alignItems="flex-start" gap={2}>
            <TextField
              size="small"
              value={intervention?.code || ''}
              onChange={handleChange('code')}
              required
              placeholder="Unique identifier"
              helperText="Cannot be changed after creation"
              InputProps={{ readOnly: !canEdit }}
              sx={{ flex: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={intervention?.is_default || false}
                  onChange={(e) => onChange('is_default', e.target.checked)}
                  disabled={!canEdit}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Default</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto-assign new users to this intervention
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
              {intervention?.code}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={intervention?.is_default || false}
                  onChange={(e) => onChange('is_default', e.target.checked)}
                  disabled={!canEdit}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Default</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto-assign new users to this intervention
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />
          </Box>
        )}

        {/* Name */}
        <Typography variant="body2" color="text.secondary">Name</Typography>
        <TextField
          size="small"
          value={intervention?.name || ''}
          onChange={handleChange('name')}
          required
          InputProps={{ readOnly: !canEdit }}
        />

        {/* Target Population */}
        <Typography variant="body2" color="text.secondary">Population</Typography>
        <TextField
          size="small"
          value={intervention?.target_population || ''}
          onChange={handleChange('target_population')}
          placeholder="e.g., Breast cancer patients undergoing chemotherapy"
          InputProps={{ readOnly: !canEdit }}
        />

        {/* Status */}
        <Typography variant="body2" color="text.secondary">Status</Typography>
        <ToggleButtonGroup
          value={intervention?.status || 'draft'}
          exclusive
          onChange={(_, val) => { if (val && canEdit) onChange('status', val) }}
          size="small"
        >
          <ToggleButton value="draft">Draft</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
          <ToggleButton value="template">Template</ToggleButton>
        </ToggleButtonGroup>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'start', mt: 1 }}>Description</Typography>
        <TextField
          size="small"
          value={intervention?.description || ''}
          onChange={handleChange('description')}
          multiline
          rows={3}
          InputProps={{ readOnly: !canEdit }}
        />
      </Box>

      {/* Settings JSON */}
      {intervention?.settings && Object.keys(intervention.settings).length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1 }}>
            Settings
          </Typography>
          <TextField
            size="small"
            value={JSON.stringify(intervention.settings, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value)
                onChange('settings', parsed)
              } catch (err) {
                // Invalid JSON, don't update
              }
            }}
            fullWidth
            multiline
            rows={6}
            InputProps={{
              readOnly: !canEdit,
              sx: { fontFamily: 'monospace', fontSize: '0.8125rem' },
            }}
          />
        </>
      )}
    </Box>
  )
}

export default OverviewTab
